import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiLoader } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import { useMembership } from '../../contexts/MembershipContext';

const ResumeUploadTooltip = ({ hasResume }) => {
  const [show, setShow] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { refreshCreditBalance } = useMembership();

  useEffect(() => {
    if (!hasResume) {
      const timer = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [hasResume]);

  if (!show || hasResume) return null;

  const handleFile = async (file) => {
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      toast.error('Please upload a PDF, DOCX, or DOC file');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', 'en');
      const response = await resumeApi.uploadResume(formData);
      await refreshCreditBalance();
      toast.success('Resume uploaded!');
      setShow(false);
      navigate(`/resumes/${response.data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Not enough credits.');
      } else {
        toast.error(err.response?.data?.detail || 'Upload failed.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed bottom-24 right-24 bg-white p-4 rounded-lg shadow-lg max-w-xs animate-bounce-slow z-50">
      <div className="relative">
        <div className="absolute -bottom-8 right-12 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-white"></div>
        
        <div className="text-gray-800">
          <h4 className="font-medium mb-2">Upload Your Resume!</h4>
          <p className="text-sm text-gray-600 mb-3">
            Start your journey by uploading your resume to unlock personalized job recommendations.
          </p>
          <input
            type="file"
            className="hidden"
            ref={fileRef}
            accept=".pdf,.docx,.doc"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFile(e.target.files[0]);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => uploading ? null : fileRef.current?.click()}
            disabled={uploading}
            className="w-full bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 transition-colors disabled:opacity-60 inline-flex items-center justify-center"
          >
            {uploading ? (
              <><FiLoader className="animate-spin mr-2 h-4 w-4" /> Uploading...</>
            ) : (
              <><FiUpload className="mr-2 h-4 w-4" /> Choose File to Upload</>
            )}
          </button>
          <button
            onClick={() => {
              setShow(false);
              navigate('/resumes/upload');
            }}
            className="w-full mt-2 text-primary-600 text-sm hover:text-primary-700 font-medium"
          >
            More options
          </button>
          <button
            onClick={() => setShow(false)}
            className="w-full mt-1 text-gray-400 text-xs hover:text-gray-600"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadTooltip;
